import { useAuth0 } from '@auth0/auth0-react';
import { openInNewTab } from '@strata/helper-functions';
import { TermsOfUseDialog } from '@strata/react-dialog';
import { Header as StrataHeader } from '@strata/react-header';
import { BaseNotification } from '@strata/react-notifications';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getHasAcceptedTermsOfUse, postAcceptTermsOfUse } from 'api/authAPI';
import { UserNotificationSettingsDialog } from 'components/header/dialogs/UserNotificationSettingsDialog';
import { UserProfileDialog } from 'components/header/dialogs/UserProfileDialog';
import { FeatureCarousel } from 'components/header/FeatureCarousel';
import { NotificationComponentMap } from 'components/notifications/NotificationComponentMap';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useGetHomeScreenMessagesQuery } from 'store/api/announcementApi';
import {
    clearIncomingNotification,
    updateNotificationStatus,
    updateNotificationStatuses,
} from 'store/notification/notificationSlice';

export const Header = () => {
    const { showAppVersion } = useFlags();
    const { isAuthenticated, logout } = useAuth0();
    const dispatch = useDispatch();

    const navTabs = isAuthenticated
        ? [
              {
                  label: 'Referral',
                  to: AppRoutes.referralForm,
              },
              {
                  label: 'Referral History',
                  to: AppRoutes.referralHistory,
              },
          ]
        : [];

    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const [showUserNotificationSettingsDialog, setShowNotificationSettingsDialog] = useState(false);
    const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);

    // Load home screen messages & carousels on login
    useGetHomeScreenMessagesQuery(undefined, { skip: !isAuthenticated });

    // Check if user has accepted terms of use on login
    useEffect(() => {
        void (async () => {
            try {
                if (isAuthenticated) {
                    const result = await getHasAcceptedTermsOfUse();
                    setShowTermsDialog(!result?.accepted);
                }
            } catch (err) {
                handleError('Error retrieving terms acceptance status', err);
            }
        })();
    }, [isAuthenticated]);

    const onAcceptTermsClick = () => {
        setShowTermsDialog(false);
        postAcceptTermsOfUse();
    };

    const { notifications, incomingNotification } = useSelector((state) => state.notification);

    // Prevent incoming notifications from re-appearing
    useEffect(() => {
        dispatch(clearIncomingNotification());
    }, [incomingNotification, dispatch]);

    const onNotificationsDropdownClose = () => {
        const newlySeenIds = notifications
            .filter((notification) => notification.status === BaseNotification.Statuses.NEW)
            .map((notification) => notification.id);
        if (newlySeenIds.length > 0) {
            dispatch(updateNotificationStatuses(BaseNotification.Statuses.SEEN, newlySeenIds));
        }
    };
    const onNotificationClick = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.INTERACTED, id));
    };
    const onRemoveNotificationClick = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.DELETED, id));
    };
    const onClearAllNotificationsClick = () => {
        const idList = notifications.map((notification) => notification.id);
        dispatch(updateNotificationStatuses(BaseNotification.Statuses.DELETED, idList));
    };

    const onLogOutClick = () => {
        sessionStorage.clear();
        logout({ logoutParams: { returnTo: `${window.location.origin}${AppRoutes.loggedOut}` } });
    };

    return (
        <>
            <StrataHeader
                navBar={navTabs}
                onProfileClick={() => setShowUserProfileDialog(true)}
                onNotificationSettingsClick={() => setShowNotificationSettingsDialog(true)}
                onLogOutClick={onLogOutClick}
                showNotifications={isAuthenticated}
                notifications={notifications}
                incomingNotification={incomingNotification}
                NotificationComponentMap={NotificationComponentMap}
                onNotificationClick={onNotificationClick}
                onRemoveNotificationClick={onRemoveNotificationClick}
                onClearAllNotificationsClick={onClearAllNotificationsClick}
                onNotificationsDropdownClose={onNotificationsDropdownClose}
                showProfileDropdown={isAuthenticated}
                onTermsOfUseClick={() => openInNewTab(AppRoutes.termsOfUse)}
                onPrivacyPolicyClick={() => openInNewTab(AppRoutes.privacyPolicy)}
                appVersion={showAppVersion && process.env.REACT_APP_VERSION}
                fmhEnvironmentForTag={process.env.REACT_APP_ENV}
            />
            {isAuthenticated && (
                <>
                    <TermsOfUseDialog
                        isOpen={showTermsDialog}
                        onAcceptClick={onAcceptTermsClick}
                        onSignOutClick={onLogOutClick}
                    />
                    {!showTermsDialog && <FeatureCarousel />}
                    <UserNotificationSettingsDialog
                        onSecondaryButtonClick={() => setShowNotificationSettingsDialog(false)}
                        isOpen={showUserNotificationSettingsDialog}
                    />
                    <UserProfileDialog
                        onSecondaryButtonClick={() => setShowUserProfileDialog(false)}
                        isOpen={showUserProfileDialog}
                    />
                </>
            )}
        </>
    );
};
